import React, {
  useRef,
  useMemo,
} from "react";

import TimelineHeader from "./partials/TimelineHeader";
import TimelineLines from "./partials/TimelineLines";
import TimelineNow from "./partials/TimelineNow";
import TimelineRow from "./partials/TimelineRow";

import styles from "./Timeline.module.scss";
import { useBodyResize } from "./hooks/useBodyResize";

export interface TimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  start: Date;
  end: Date;
  groups: Sentinel.Timeline.GroupedEvent[];
  intervalInMinutes?: number;
  groupHeight?: number;
  title?: string;
  height?: string;
  overlapEnd?: Date;
  fixedWidth?: number;
}

const Timeline: React.FC<TimelineProps> = ({
  start,
  end,
  groups,
  intervalInMinutes = 30,
  groupHeight = 70,
  title,
  height = "75%",
  overlapEnd,
  fixedWidth,
  className,
  ...rest
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useBodyResize(containerRef);
  const size = fixedWidth ?? containerSize;

  const hoursLabels = useMemo(() => {
    const output: Date[] = [];

    const current = new Date(start);

    while (current <= end) {
      output.push(new Date(current));
      current.setMinutes(current.getMinutes() + intervalInMinutes);
    }

    return output;
  }, [start, end, intervalInMinutes]);

  return (
    <div style={{ height, display: "flex" }}>

      <div
        ref={containerRef}
        className={[styles.timeline, className].filter(Boolean).join(" ")}
        {...rest}
      >
        <TimelineHeader
          hoursLabels={hoursLabels} />

        <div className={styles.body}>
          <TimelineLines
            size={hoursLabels.length} />

          <TimelineNow
            size={size}
            start={start}
            end={end}
            overlapEnd={overlapEnd} />

          <div className={styles.rows}>
            {groups.map((group) => (
              <TimelineRow
                key={group.groupId}
                size={size}
                groupHeight={groupHeight}
                start={start}
                end={end}
                group={group} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Timeline;